<template>
    <div class="web-app-detail-page">
        <AppBreadCrumb
            :paths="[
                { title: $t('message.integrations') },
                { title: $t('webAppsPage.breadcrumbTitle'), path: '/integrations/webapps' },
                { title: isEditMode ? $t('webAppsPage.breadcrumbEdit') : $t('webAppsPage.breadcrumbNew') },
            ]"
        />

        <a-card :loading="loading" class="p-2">
            <h2>{{ isEditMode ? $t("webAppsPage.headingEdit") : $t("webAppsPage.headingNew") }}</h2>

            <!-- Supsis Code Promotion -->
            <a-card class="promo-card" style="margin-bottom: 24px">
                <div class="supsis-code-promo">
                    <div class="promo-left">
                        <div class="promo-icon">
                            <span class="material-icons">code</span>
                        </div>
                        <div class="promo-text">
                            <h3>{{ $t("webAppsPage.promoTitle") }}</h3>
                            <p>{{ $t("webAppsPage.promoDescription") }}</p>
                            <ul>
                                <li>{{ $t("webAppsPage.promoBullet1") }}</li>
                                <li>{{ $t("webAppsPage.promoBullet2") }}</li>
                                <li>{{ $t("webAppsPage.promoBullet3") }}</li>
                                <li>{{ $t("webAppsPage.promoBullet4") }}</li>
                            </ul>
                        </div>
                    </div>
                    <div class="promo-right">
                        <a-button type="primary" size="large" @click="openCodeSupsis">
                            <span class="material-icons">rocket_launch</span>
                            {{ $t("webAppsPage.ctaCodeSupsis") }}
                        </a-button>
                    </div>
                </div>
            </a-card>

            <!-- Gereksinimler -->
            <a-alert
                :message="$t('webAppsPage.requirementsTitle')"
                type="info"
                show-icon
                closable
                style="margin-bottom: 24px"
            >
                <template slot="description">
                    <ul style="margin: 8px 0; padding-left: 20px">
                        <li v-html="$t('webAppsPage.requirementRootIndex')"></li>
                        <li v-html="$t('webAppsPage.requirementMaxSize')"></li>
                        <li>{{ $t("webAppsPage.requirementSupported") }}</li>
                        <li>{{ $t("webAppsPage.requirementSlug") }}</li>
                    </ul>
                </template>
            </a-alert>

            <a-form :form="form" @submit.prevent="handleSubmit">
                <!-- İkon ve Renk -->
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="İkon">
                            <MaterialIconPicker v-model="formData.icon" />
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="İkon Rengi">
                            <color-picker v-model="formData.iconColor" />
                            <a-input v-model="formData.iconColor" placeholder="#6b727d" style="margin-top: 8px" />
                        </a-form-item>
                    </a-col>
                </a-row>

                <!-- Ad -->
                <a-form-item label="Web App Adı" required>
                    <a-input v-model="formData.name" placeholder="Örn: CRM Dashboard" />
                </a-form-item>

                <!-- Açıklama -->
                <a-form-item label="Açıklama">
                    <a-textarea
                        v-model="formData.description"
                        placeholder="Web App'in ne yaptığını açıklayın"
                        :rows="3"
                    />
                </a-form-item>

                <!-- Slug -->
                <a-form-item label="Slug (Subdomain)" required>
                    <a-input
                        v-model="formData.slug"
                        placeholder="ornek-webapp"
                        @input="handleSlugInput"
                        @blur="checkSlugAvailability"
                    >
                        <a-icon
                            v-if="slugStatus"
                            slot="suffix"
                            :type="slugStatus === 'available' ? 'check-circle' : 'close-circle'"
                            :style="{ color: slugStatus === 'available' ? '#52c41a' : '#f5222d' }"
                        />
                        <a-icon v-else-if="checkingSlug" slot="suffix" type="loading" />
                    </a-input>
                    <div class="slug-info">
                        URL: <strong>{{ formData.slug || "slug" }}.webapp.supsis.ai</strong>
                    </div>
                    <div v-if="slugError" class="slug-error">
                        <span class="material-icons">error</span>
                        {{ slugError }}
                    </div>
                    <div class="slug-rules">
                        <small>
                            Sadece küçük harf, rakam ve tire (-) kullanılabilir. Min 3, max 63 karakter.
                        </small>
                    </div>
                </a-form-item>

                <!-- Version -->
                <a-form-item label="Version">
                    <a-input v-model="formData.version" placeholder="1.0.0" style="width: 200px" />
                </a-form-item>

                <!-- Public (sadece Supsis sitesi için) -->
                <a-form-item v-if="isSupsisSite" label="Public Yap">
                    <a-switch v-model="formData.isPublic">
                        <span slot="checkedChildren">Public</span>
                        <span slot="unCheckedChildren">Private</span>
                    </a-switch>
                    <div style="font-size: 12px; color: #999; margin-top: 4px">
                        Public yapılan app'ler tüm Supsis sitelerinde görünür
                    </div>
                </a-form-item>

                <!-- Zip Upload -->
                <a-form-item label="Proje Dosyası (Zip)" :required="!isEditMode">
                    <a-upload-dragger
                        :before-upload="beforeUpload"
                        :custom-request="customUpload"
                        accept=".zip"
                        :show-upload-list="false"
                        :disabled="uploading"
                    >
                        <p class="ant-upload-drag-icon">
                            <span class="material-icons">{{ uploadedFile ? "check_circle" : "upload_file" }}</span>
                        </p>
                        <p class="ant-upload-text">
                            {{ uploadedFile ? uploadedFile.name : "Zip dosyasını sürükleyin veya tıklayın" }}
                        </p>
                        <p class="ant-upload-hint">
                            {{ isEditMode ? "Yeni zip yüklemek mevcut dosyaların üzerine yazacak" : "Maksimum 100MB, sadece .zip dosyası" }}
                        </p>
                    </a-upload-dragger>

                    <a-progress
                        v-if="uploading"
                        :percent="displayUploadProgress"
                        status="active"
                        style="margin-top: 16px"
                    />

                    <div v-if="formData.metadata && formData.metadata.fileCount" class="upload-info">
                        <a-alert type="success" show-icon>
                            <template slot="message">
                                Proje Yüklendi: {{ formData.metadata.fileCount }} dosya
                                ({{ formatSize(formData.metadata.totalSize) }})
                                <span v-if="formData.metadata.uploadedAt">
                                    - {{ $moment(formData.metadata.uploadedAt).fromNow() }}
                                </span>
                            </template>
                        </a-alert>
                    </div>
                </a-form-item>

                <!-- Default Query Parametreleri -->
                <a-form-item label="Default Query Parametreleri">
                    <div class="query-params-section">
                        <p class="query-params-hint">
                            Bu uygulama web sekmesi olarak kurulduğunda otomatik eklenecek URL parametreleri
                        </p>
                        <div class="query-params-list">
                            <div
                                v-for="(param, index) in formData.defaultQueryParams"
                                :key="index"
                                class="query-param-row"
                            >
                                <a-input v-model="param.key" placeholder="Key" size="small" style="width: 120px" />
                                <span class="param-equals">=</span>
                                <a-input
                                    v-model="param.value"
                                    placeholder="Value veya ${dinamik}"
                                    size="small"
                                    style="flex: 1"
                                />
                                <a-select
                                    v-model="param.dynamicValue"
                                    placeholder="Dinamik"
                                    size="small"
                                    style="width: 140px"
                                    @change="(val) => handleDynamicValueChange(index, val)"
                                >
                                    <a-select-opt-group label="Manuel">
                                        <a-select-option value="">Manuel</a-select-option>
                                    </a-select-opt-group>
                                    <a-select-opt-group label="Kullanıcı">
                                        <a-select-option value="${user.name}">Adı</a-select-option>
                                        <a-select-option value="${user.email}">Email</a-select-option>
                                        <a-select-option value="${user.id}">ID</a-select-option>
                                        <a-select-option value="${user.token}">Token</a-select-option>
                                    </a-select-opt-group>
                                    <a-select-opt-group label="Site">
                                        <a-select-option value="${site.id}">Site ID</a-select-option>
                                    </a-select-opt-group>
                                    <a-select-opt-group label="Kişi">
                                        <a-select-option value="${contact.id}">ID</a-select-option>
                                        <a-select-option value="${contact.fullname}">Ad Soyad</a-select-option>
                                        <a-select-option value="${contact.email}">E-posta</a-select-option>
                                        <a-select-option value="${contact.phone}">Telefon</a-select-option>
                                    </a-select-opt-group>
                                </a-select>
                                <a-button
                                    type="link"
                                    size="small"
                                    @click="removeQueryParam(index)"
                                    class="remove-param-btn"
                                >
                                    <span class="material-icons">close</span>
                                </a-button>
                            </div>
                        </div>
                        <a-button type="dashed" size="small" @click="addQueryParam" class="add-param-btn">
                            <span class="material-icons">add</span>
                            Parametre Ekle
                        </a-button>
                    </div>
                </a-form-item>

                <!-- Durum -->
                <a-form-item label="Durum">
                    <a-switch v-model="formData.enabled">
                        <span slot="checkedChildren">Aktif</span>
                        <span slot="unCheckedChildren">Pasif</span>
                    </a-switch>
                </a-form-item>

                <!-- Butonlar -->
                <a-form-item>
                    <a-space>
                        <a-button type="primary" html-type="submit" :loading="saving" :disabled="uploading">
                            {{ isEditMode ? "Güncelle" : "Oluştur" }}
                        </a-button>
                        <a-button @click="$router.push('/integrations/webapps')">İptal</a-button>
                    </a-space>
                </a-form-item>
            </a-form>
        </a-card>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb.vue";
import MaterialIconPicker from "../Module/MaterialIconPicker.vue";

const SUPSIS_SITE_ID = "5d14ee322d95ad001ebdf213";

export default {
    name: "WebAppDetail",
    components: {
        AppBreadCrumb,
        MaterialIconPicker,
    },
    data() {
        return {
            form: this.$form.createForm(this),
            loading: false,
            saving: false,
            uploading: false,
            uploadProgress: 0,
            uploadedFile: null,
            checkingSlug: false,
            slugStatus: null,
            slugError: null,
            formData: {
                name: "",
                slug: "",
                description: "",
                version: "1.0.0",
                icon: "web",
                iconColor: "#6b727d",
                isPublic: false,
                enabled: true,
                metadata: null,
                defaultQueryParams: [],
            },
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            storeUploadProgress: (state) => state.webapps.uploadProgress,
        }),
        isEditMode() {
            return !!this.$route.params.id;
        },
        isSupsisSite() {
            return this.site && this.site._id === SUPSIS_SITE_ID;
        },
        displayUploadProgress() {
            return this.storeUploadProgress || this.uploadProgress;
        },
    },
    methods: {
        ...mapActions({
            getWebApp: "webapps/getWebApp",
            createWebApp: "webapps/createWebApp",
            updateWebApp: "webapps/updateWebApp",
            checkSlug: "webapps/checkSlugAvailability",
            uploadProject: "webapps/uploadProject",
        }),
        handleSlugInput() {
            // Otomatik lowercase ve sanitize
            this.formData.slug = this.formData.slug
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, "-")
                .replace(/--+/g, "-")
                .replace(/^-|-$/g, "")
                .substring(0, 63);
            
            this.slugStatus = null;
            this.slugError = null;
        },
        async checkSlugAvailability() {
            if (!this.formData.slug || this.formData.slug.length < 3) {
                this.slugStatus = null;
                this.slugError = "Slug en az 3 karakter olmalı";
                return;
            }

            this.checkingSlug = true;
            this.slugError = null;

            try {
                const result = await this.checkSlug({
                    slug: this.formData.slug,
                    excludeId: this.isEditMode ? this.$route.params.id : null,
                });

                if (result.available) {
                    this.slugStatus = "available";
                    this.slugError = null;
                } else {
                    this.slugStatus = "unavailable";
                    this.slugError = result.error || "Bu slug kullanılamıyor";
                }
            } catch (error) {
                this.slugStatus = "error";
                this.slugError = "Slug kontrolü yapılamadı";
                console.error(error);
            } finally {
                this.checkingSlug = false;
            }
        },
        beforeUpload(file) {
            const isZip = file.type === "application/zip" || file.name.endsWith(".zip");
            if (!isZip) {
                this.$message.error("Sadece .zip dosyaları yüklenebilir!");
                return false;
            }

            const isLt100M = file.size / 1024 / 1024 < 100;
            if (!isLt100M) {
                this.$message.error("Dosya boyutu 100MB'dan küçük olmalı!");
                return false;
            }

            return true; // true döndür ki customUpload çağrılsın
        },
        async customUpload(uploadInfo) {
            // uploadInfo: { file, onSuccess, onError, onProgress }
            const file = uploadInfo.file;
            
            console.log("customUpload called", { 
                isEditMode: this.isEditMode, 
                fileName: file.name,
                fileSize: file.size 
            });

            this.uploadedFile = file;

            // Edit modda direkt upload yap
            if (this.isEditMode) {
                this.uploading = true;
                this.uploadProgress = 0;

                try {
                    const result = await this.uploadProject({
                        id: this.$route.params.id,
                        file: file,
                    });

                    this.$message.success("Proje başarıyla yüklendi!");
                    
                    // Metadata'yı güncelle
                    await this.loadWebApp();
                    
                    // Ant Design'a başarılı bildir
                    if (uploadInfo.onSuccess) {
                        uploadInfo.onSuccess(result, file);
                    }
                } catch (error) {
                    this.$message.error("Proje yüklenirken bir hata oluştu: " + (error.response?.data?.error || error.message));
                    console.error("Upload error:", error);
                    
                    // Ant Design'a hata bildir
                    if (uploadInfo.onError) {
                        uploadInfo.onError(error);
                    }
                } finally {
                    this.uploading = false;
                    this.uploadProgress = 0;
                }
            } else {
                // Create modda sadece dosyayı kaydet, submit'te upload yapılacak
                this.$message.info("Zip dosyası seçildi. Oluştur butonuna tıklayın.");
                
                if (uploadInfo.onSuccess) {
                    uploadInfo.onSuccess("File selected", file);
                }
            }
        },
        async loadWebApp() {
            if (!this.isEditMode) return;

            this.loading = true;
            try {
                const webApp = await this.getWebApp(this.$route.params.id);
                this.formData = {
                    ...webApp,
                    defaultQueryParams: (webApp.defaultQueryParams || []).map((param) => ({
                        ...param,
                        dynamicValue: param.isDynamic ? param.value : "",
                    })),
                };
            } catch (error) {
                this.$message.error("Web App yüklenirken bir hata oluştu");
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async handleSubmit() {
            // Validasyon
            if (!this.formData.name || !this.formData.slug) {
                this.$message.error("Lütfen zorunlu alanları doldurun");
                return;
            }

            if (this.slugStatus === "unavailable") {
                this.$message.error("Slug kullanılamıyor, lütfen başka bir slug seçin");
                return;
            }

            if (!this.isEditMode && !this.uploadedFile) {
                this.$message.error("Lütfen bir zip dosyası yükleyin");
                return;
            }

            this.saving = true;
            try {
                const cleanedQueryParams = this.formData.defaultQueryParams.map((param) => ({
                    key: param.key,
                    value: param.value,
                    isDynamic: param.isDynamic || false,
                }));

                const data = {
                    name: this.formData.name,
                    slug: this.formData.slug,
                    description: this.formData.description,
                    version: this.formData.version,
                    icon: this.formData.icon,
                    iconColor: this.formData.iconColor,
                    isPublic: this.formData.isPublic,
                    enabled: this.formData.enabled,
                    defaultQueryParams: cleanedQueryParams,
                };

                let result;

                if (this.isEditMode) {
                    result = await this.updateWebApp({
                        id: this.$route.params.id,
                        data,
                    });
                    this.$message.success("Web App güncellendi");
                } else {
                    result = await this.createWebApp(data);
                    this.$message.success("Web App oluşturuldu");

                    // Yeni oluşturulduysa zip upload yap
                    if (this.uploadedFile && result._id) {
                        this.uploading = true;
                        try {
                            await this.uploadProject({
                                id: result._id,
                                file: this.uploadedFile,
                            });
                            this.$message.success("Proje dosyaları yüklendi!");
                        } catch (uploadError) {
                            this.$message.error("Proje yüklenirken hata: " + uploadError.message);
                        } finally {
                            this.uploading = false;
                        }
                    }
                }

                this.$router.push("/integrations/webapps");
            } catch (error) {
                this.$message.error("İşlem sırasında bir hata oluştu: " + error.message);
                console.error(error);
            } finally {
                this.saving = false;
            }
        },
        formatSize(bytes) {
            if (!bytes) return "0 Bytes";
            const k = 1024;
            const sizes = ["Bytes", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
        },
        openCodeSupsis() {
            window.open("https://code.supsis.ai", "_blank");
        },
        addQueryParam() {
            this.formData.defaultQueryParams.push({
                key: "",
                value: "",
                isDynamic: false,
                dynamicValue: "",
            });
        },
        removeQueryParam(index) {
            this.formData.defaultQueryParams.splice(index, 1);
        },
        handleDynamicValueChange(index, value) {
            if (value) {
                this.formData.defaultQueryParams[index].value = value;
                this.formData.defaultQueryParams[index].isDynamic = true;
            } else {
                this.formData.defaultQueryParams[index].isDynamic = false;
            }
        },
    },
    mounted() {
        this.loadWebApp();
    },
    watch: {
        storeUploadProgress(val) {
            console.log("Upload progress:", val);
        },
    },
};
</script>

<style lang="scss" scoped>
.web-app-detail-page {
    

    h2 {
        margin-bottom: 24px;
    }

    .slug-info {
        margin-top: 8px;
        font-size: 13px;
        color: #666;

        strong {
            color: #1890ff;
            font-family: monospace;
        }
    }

    .slug-error {
        margin-top: 8px;
        color: #f5222d;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 4px;

        .material-icons {
            font-size: 16px;
        }
    }

    .slug-rules {
        margin-top: 4px;
        color: #999;
    }

    .query-params-section {
        .query-params-hint {
            font-size: 13px;
            color: #666;
            margin-bottom: 12px;
        }

        .query-params-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 12px;
        }

        .query-param-row {
            display: flex;
            align-items: center;
            gap: 8px;

            .param-equals {
                color: #94a3b8;
                font-weight: 500;
            }

            .remove-param-btn {
                padding: 0;
                height: auto;
                color: #94a3b8;

                &:hover {
                    color: #ef4444;
                }

                .material-icons {
                    font-size: 18px;
                }
            }
        }

        .add-param-btn {
            display: flex;
            align-items: center;
            gap: 6px;

            .material-icons {
                font-size: 16px;
            }
        }
    }

    .upload-info {
        margin-top: 16px;
    }

    .ant-upload-drag-icon {
        .material-icons {
            font-size: 64px;
            color: #1890ff;
        }
    }

    .promo-card {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;

        .supsis-code-promo {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 24px;
            color: white;

            .promo-left {
                display: flex;
                gap: 20px;
                align-items: flex-start;
                flex: 1;

                .promo-icon {
                    .material-icons {
                        font-size: 64px;
                        color: rgba(255, 255, 255, 0.9);
                    }
                }

                .promo-text {
                    h3 {
                        margin: 0 0 8px 0;
                        font-size: 20px;
                        color: white;
                    }

                    p {
                        margin: 0 0 12px 0;
                        font-size: 14px;
                        color: rgba(255, 255, 255, 0.9);
                    }

                    ul {
                        margin: 0;
                        padding-left: 20px;
                        font-size: 13px;
                        color: rgba(255, 255, 255, 0.85);

                        li {
                            margin-bottom: 4px;
                        }
                    }
                }
            }

            .promo-right {
                .ant-btn {
                    height: auto;
                    padding: 12px 24px;
                    font-size: 16px;
                    font-weight: 600;

                    .material-icons {
                        font-size: 20px;
                        margin-right: 8px;
                        vertical-align: middle;
                    }
                }
            }
        }
    }
}

// Override ant-card background for promo
.promo-card {
    ::v-deep .ant-card-body {
        background: transparent;
    }
}
</style>
